import React, { FC, ReactElement, useEffect, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Form, Input, message } from "antd";
import { MobileOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import axios from "axios";

import { toast } from "../../utils/toast";

import { selectErrorMessage } from "../../redux-toolkit/auth/auth-selector";
import { resetAuthState } from "../../redux-toolkit/auth/auth-slice";
import { activateAccount } from "../../redux-toolkit/auth/auth-thunks";
import { selectSuccessMessage } from "../../redux-toolkit/user/user-selector";
import { BASE_URL } from "../../constants/urlConstants";

import finkartLogo from "../../assets/images/finkart_wordmark.png";
import finllieLogo from "../../assets/images/finllie.png";
import { fetchUserInfo } from "../../redux-toolkit/user/user-thunks";
import { resetOrderState, setOrderLoadingState } from "../../redux-toolkit/order/order-slice";
import { LoadingStatus } from "../../types/types";
import { resetCartState } from "../../redux-toolkit/cart/cart-slice";
import "./Login.css";

type OtpStep = "phone" | "verify";
const OTP_RESEND_SECONDS = 30;

const clearCacheForNewUser = (keepKeys: string[]) => {
  Object.keys(localStorage).forEach((k) => {
    if (!keepKeys.includes(k)) localStorage.removeItem(k);
  });
  sessionStorage.clear();
  localStorage.removeItem("persist:root");
};

const Login: FC = (): ReactElement => {
  const dispatch       = useDispatch();
  const history        = useHistory();
  const params         = useParams<{ code: string }>();
  const errorMessage   = useSelector(selectErrorMessage);
  const successMessage = useSelector(selectSuccessMessage);

  const [form]         = Form.useForm();
  const [otpStep,      setOtpStep]      = useState<OtpStep>("phone");
  const [otpPhone,     setOtpPhone]     = useState("");
  const [sendingOtp,   setSendingOtp]   = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendTimer,  setResendTimer]  = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ✅ Added: cleanup on unmount
  useEffect(() => {
    window.scrollTo(0, 0);
    if (params.code) dispatch(activateAccount(params.code));
    return () => {
      dispatch(resetAuthState());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startResendTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current); // clear any existing
    setResendTimer(OTP_RESEND_SECONDS);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

    const handleSendOtp = async (values: any) => {
      const phone = values.phone?.trim();
      if (!phone) return;
      setSendingOtp(true);
      try {
        await axios.post(`${BASE_URL}/api/otp/send`, { phone });
        setOtpPhone(phone);
        setOtpStep("verify");
        startResendTimer();
       // message.success("OTP sent to your mobile!");
        toast("success", "OTP sent to your mobile!");
      } catch (err) {
        // Toast already shown by the global axios interceptor — don't duplicate it here.
      } finally {
        setSendingOtp(false);
      }
    };

  // ── Verify OTP ──
  const handleVerifyOtp = async (values: any) => {
    setVerifyingOtp(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/otp/verify`, {
        phone: otpPhone,
        otp:   values.otp,
      });
      const { token, role, mobile, distributorId, distId, distName, name } = res.data;

      clearCacheForNewUser([]);
      localStorage.setItem("token",  token);
      localStorage.setItem("role",   role);
      localStorage.setItem("mobile", mobile);
      localStorage.setItem("name",   name);

      if (role === "3") {
        // Distributor
        localStorage.setItem("distributorId", distributorId);
        localStorage.setItem("distId",        distId);
        localStorage.setItem("distName",      distName ?? "");
      } else {
        // Retailer
        localStorage.setItem("distId",   distributorId ?? "");
        localStorage.setItem("distName", distName      ?? "");
      }

      window.dispatchEvent(new Event("storage"));
      dispatch(fetchUserInfo());
      dispatch(resetCartState());
      dispatch(resetOrderState());
      toast("success", "Login successful!");
      history.push("/");
    } catch (err: any) {
      // ✅ Show backend message e.g. "Invalid OTP" / "OTP expired"
      const msg = err?.response?.data?.message || "Invalid OTP. Please try again.";
     // message.error(msg);
      toast("error", msg);
    } finally {
      setVerifyingOtp(false);
    }
  };

  // ── Resend OTP ──
  const handleResendOtp = async () => {
    if (resendTimer > 0 || !otpPhone) return;
    setSendingOtp(true);
    try {
      await axios.post(`${BASE_URL}/api/otp/send`, { phone: otpPhone });
      startResendTimer();
      toast("success", "OTP resent!");
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to resend OTP.";
     // message.error(msg);
      toast("error", msg);
    } finally {
      setSendingOtp(false);
    }
  };

  return (
    <div className="lp-root">

      {/* ── HERO ── */}
      <div className="lp-hero">
        <div className="lp-hero-blob lp-hero-blob--1" />
        <div className="lp-hero-blob lp-hero-blob--2" />
        <div className="lp-hero-blob lp-hero-blob--3" />

        <div className="lp-hero-content">

          {/* Finkart logo */}
          <div className="lp-brand">
            <img src={finkartLogo} alt="FinKart" className="lp-finkart-logo" />
          </div>

          {/* Headline */}
          <h1 className="lp-headline">
            Order <span className="lp-accent">pure water</span><br />
            delivered fast
          </h1>
          <p className="lp-hero-sub">
            Premium water, same-day delivery<br />across Bengaluru
          </p>

          {/* Finllie pill */}
          <div className="lp-finllie-pill">
            <span>Order</span>
            <img src={finllieLogo} alt="Finllie" className="lp-finllie-logo" />
            <span>products from FinKart</span>
          </div>

          {/* Stats */}
          <div className="lp-stats">
            <div className="lp-stat">
              <span className="lp-stat-num">12K+</span>
              <span className="lp-stat-lbl">Customers</span>
            </div>
            <div className="lp-stat-divider" />
            <div className="lp-stat">
              <span className="lp-stat-num">15K+</span>
              <span className="lp-stat-lbl">Bottles daily</span>
            </div>
            <div className="lp-stat-divider" />
            <div className="lp-stat">
              <span className="lp-stat-num">100%</span>
              <span className="lp-stat-lbl">Pure</span>
            </div>
            <div className="lp-stat-divider" />
            <div className="lp-stat">
              <span className="lp-stat-num">4.8★</span>
              <span className="lp-stat-lbl">Rated</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── BOTTOM SHEET ── */}
      <div className="lp-sheet">
        <div className="lp-sheet-handle" />

        {errorMessage   && <Alert message={errorMessage}   type="error"   showIcon className="lp-alert" />}
        {successMessage && <Alert message={successMessage} type="success" showIcon className="lp-alert" />}

        {/* ── Phone step ── */}
        {otpStep === "phone" && (
          <>
            <h2 className="lp-sheet-title">Start ordering now</h2>
            <p className="lp-sheet-sub">Just one quick step</p>

            <Form form={form} layout="vertical" onFinish={handleSendOtp}>
              <Form.Item
                name="phone"
                rules={[
                  { required: true,                          message: "Enter your mobile number" },
                  { pattern: /^\d{10}$/, message: "Enter a valid 10-digit number" },
                ]}
              >
                <Input
                  prefix={<MobileOutlined className="lp-input-icon" />}
                  addonBefore={<span className="lp-country-code">🇮🇳 +91</span>}
                  placeholder="Enter mobile number"
                  size="large"
                  maxLength={10}
                  className="lp-phone-input"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 12 }}>
                <button type="submit" className="lp-otp-btn" disabled={sendingOtp}>
                  {sendingOtp ? "Sending OTP..." : "Send OTP →"}
                </button>
              </Form.Item>
            </Form>

            <p className="lp-hint">
              <span className="lp-hint-dot">✓</span>
              Works for retailers and distributors
            </p>

            <div className="lp-footer">
              Don't have an account?{" "}
              <Link to="/registration" className="lp-signup-link">Sign up</Link>
            </div>
          </>
        )}

        {/* ── OTP verify step ── */}
        {otpStep === "verify" && (
          <>
            <h2 className="lp-sheet-title">Enter OTP</h2>
            <div className="lp-otp-sent-info">
              Sent to <strong>+91 {otpPhone}</strong>
              <button
                type="button"
                className="lp-change-btn"
                onClick={() => { setOtpStep("phone"); form.resetFields(); }}
              >
                Change
              </button>
            </div>

            <Form form={form} layout="vertical" onFinish={handleVerifyOtp}>
              <Form.Item
                name="otp"
                rules={[
                  { required: true,                         message: "Enter the OTP" },
                  { pattern: /^\d{6}$/, message: "Enter a valid 6-digit OTP" },
                ]}
              >
                <Input
                  prefix={<SafetyCertificateOutlined className="lp-input-icon" />}
                  placeholder="6-digit OTP"
                  size="large"
                  maxLength={6}
                  className="lp-phone-input"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 12 }}>
                <button type="submit" className="lp-otp-btn" disabled={verifyingOtp}>
                  {verifyingOtp ? "Verifying..." : "Verify & Login →"}
                </button>
              </Form.Item>
            </Form>

            <div className="lp-resend-row">
              {resendTimer > 0 ? (
                <span className="lp-resend-timer">Resend OTP in {resendTimer}s</span>
              ) : (
                <button
                  type="button"
                  className="lp-resend-btn"
                  onClick={handleResendOtp}
                  disabled={sendingOtp}
                >
                  Resend OTP
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;